import React, { useState, useEffect } from "react";
import { FaPlus, FaBowlingBall } from "react-icons/fa";

import { supabase } from "../../../supabaseClient";
import EventCard from "./EventsCard";
import EventForm from "./EventForm";
import EventTemplatePicker from "./EventTemplatePicker";
import EventGroupSection from "./EventGroupSection";
import { useTournamentAuth } from "../../../utils/authorization";
import InlineAlert from "../../UI/DesignStandards/Notifications/InlineAlert";
import { SecondaryButton } from "../../UI/DesignStandards";
import {
  sanitizeOperationError,
  logErrorForDebugging,
} from "../../../utility/errorSanitization";
import { sanitizeText } from "../../../utils/sanitization";
import {
  amountsFromEvent,
  filledEventFees,
  sortFees,
  syncEventFees,
} from "../../../utility/eventFees";
import { deleteOrphanedTemplateInstances } from "../../../utility/squadEventInstances";
import { useTournamentId } from "../../../context/TournamentRouteContext";
import {
  isAllEventTemplate,
  isPerSquadSidePot,
  isPrimaryEvent,
  isSidePotEvent,
} from "../../../utility/eventTemplates";

const ManageEvents = ({ onEventsChange }) => {
  const tournamentId = useTournamentId();
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showNewEventForm, setShowNewEventForm] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isPayoutFinalized, setIsPayoutFinalized] = useState(false); // Add payout locking state
  const [feeCatalog, setFeeCatalog] = useState([]);
  const [primaryFeeAmounts, setPrimaryFeeAmounts] = useState({});
  const [sidepotFeeAmounts, setSidepotFeeAmounts] = useState({});
  const [primaryFeesDirty, setPrimaryFeesDirty] = useState(false);
  const [sidepotFeesDirty, setSidepotFeesDirty] = useState(false);
  const [savingFeeGroup, setSavingFeeGroup] = useState(null);

  // TODO: Remove this feature flag after RLS testing is complete

  // Check tournament authorization
  const { canManage, loading: authLoading } = useTournamentAuth(tournamentId);

  // Fetch payout status
  const fetchPayoutStatus = async () => {
    try {
      const { data: verificationStates, error } = await supabase
        .from("verificationStates")
        .select("*")
        .eq("tournamentId", tournamentId)
        .eq("entityType", "payouts");

      if (error) throw error;

      // Handle case where no verification states exist yet
      const payoutVerification = verificationStates?.[0];
      setIsPayoutFinalized(payoutVerification?.isVerified || false);
    } catch (err) {
      // Error fetching payout status - silent fail, default to false
      setIsPayoutFinalized(false);
    }
  };

  useEffect(() => {
    if (tournamentId) {
      fetchEvents();
      fetchPayoutStatus(); // Fetch payout status on mount
    }

    // Check auth state
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsLoggedIn(!!session);
    });

    // Initial auth check
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsLoggedIn(!!session);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [tournamentId]);

  useEffect(() => {
    let cancelled = false;
    supabase
      .from("fees")
      .select("id, feeName")
      .order("feeName")
      .then(({ data, error }) => {
        if (cancelled || error) return;
        setFeeCatalog(sortFees(data || []));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (feeCatalog.length === 0) return;
    if (!primaryFeesDirty) {
      const firstPrimary = events.find(isPrimaryEvent);
      if (firstPrimary) {
        setPrimaryFeeAmounts(amountsFromEvent(firstPrimary, feeCatalog));
      }
    }
    if (!sidepotFeesDirty) {
      const firstSidePot = events.find(isSidePotEvent);
      if (firstSidePot) {
        setSidepotFeeAmounts(amountsFromEvent(firstSidePot, feeCatalog));
      }
    }
  }, [events, feeCatalog, primaryFeesDirty, sidepotFeesDirty]);

  const fetchEvents = async () => {
    try {
      setIsLoading(true);

      // Fetch events with calculation type information
      // Note: RLS automatically filters out deleted events (isDeleted = false)
      const { data: eventsData, error: eventsError } = await supabase
        .from("tournamentEvents")
        .select(
          `
          id,
          eventName,
          isPrimary,
          bowlerQuantity,
          gamesPerEvent,
          maxSharedPlayers,
          eventAbbreviation,
          tournamentEventCalculationTypeID,
          created,
          isDeleted,
          deletedAt,
          deletedBy,
          templateId,
          squadId,
          tournamentEventCalculationType (
            id,
            name,
            code,
            description
          )
        `
        )
        .eq("tournamentID", tournamentId)
        .is("templateId", null) // Only fetch tournament-wide events, not squad instances
        .order("isPrimary", { ascending: false })
        .order("created", { ascending: true });

      if (eventsError) throw eventsError;

      // Fetch templates with calculation type information
      const { data: templatesData, error: templatesError } = await supabase
        .from("eventSquadTemplate")
        .select(
          `
          id,
          eventName,
          eventAbbreviation,
          tournamentEventCalculationTypeID,
          maxSharedPlayers,
          created,
          isDeleted,
          deletedAt,
          deletedBy,
          tournamentEventCalculationType (
            id,
            name,
            code,
            description
          )
        `
        )
        .eq("tournamentID", tournamentId)
        .eq("isDeleted", false)
        .order("created", { ascending: true });

      if (templatesError) throw templatesError;

      // Fetch fees for each event
      const eventIds = eventsData.map((event) => event.id);
      const { data: feesData, error: feesError } =
        eventIds.length > 0
          ? await supabase
              .from("eventFee")
              .select(
                `
              id,
              eventsID,
              amount,
              fees (
                id,
                feeName
              )
            `
              )
              .in("eventsID", eventIds)
          : { data: [], error: null };

      if (feesError) throw feesError;

      // Fetch fees for each template
      const templateIds = templatesData.map((template) => template.id);
      const { data: templateFeesData, error: templateFeesError } =
        templateIds.length > 0
          ? await supabase
              .from("eventSquadTemplateFees")
              .select(
                `
              id,
              templateID,
              amount,
              fees (
                id,
                feeName
              )
            `
              )
              .in("templateID", templateIds)
          : { data: [], error: null };

      if (templateFeesError) throw templateFeesError;

      // Process events to include fees and calculation type name
      // Filter out deleted events for display
      const eventsWithFees = eventsData
        .filter((event) => !event.isDeleted) // Remove deleted events from display
        .map(async (event) => {
          // Mark as tournament event (not template)
          event.isTemplate = false;
          const eventFees = feesData.filter((fee) => fee.eventsID === event.id);
          const fees = eventFees.map((fee) => ({
            id: fee.id,
            amount: fee.amount,
            feeName: fee.fees?.feeName,
          }));

          // Get team count for this event (only non-deleted teams)
          const { count: teamCount } = await supabase
            .from("teams")
            .select("*", { count: "exact", head: true })
            .eq("eventsID", event.id)
            .eq("isDeleted", false);

          // Get event entry count for this event (only from non-deleted teams)
          const { count: eventEntryCount } = await supabase
            .from("eventEntry")
            .select(
              `
              *,
              teams!inner (
                id,
                isDeleted
              )
            `,
              { count: "exact", head: true }
            )
            .eq("eventsID", event.id)
            .eq("teams.isDeleted", false);

          // Check if this is an all event and fetch configuration
          const isAllEvent =
            event.tournamentEventCalculationType?.code === "allEventScratch" ||
            event.tournamentEventCalculationType?.code === "allEventHdcp";

          let allEventConfig = null;
          if (isAllEvent) {
            try {
              // Fetch all event configuration
              const { data: configData, error: configError } =
                await supabase.rpc("get_all_event_configuration", {
                  input_event_id: event.id,
                });

              if (!configError && configData && configData.length > 0) {
                const config = configData[0];

                // Fetch primary events details
                const { data: primaryEventsData, error: primaryEventsError } =
                  await supabase.rpc("get_all_event_primary_events", {
                    input_event_id: event.id,
                  });

                if (!primaryEventsError && primaryEventsData) {
                  allEventConfig = {
                    secondaryRuleType:
                      config.secondary_rule_type || "first_attempt",
                    primaryEvents: primaryEventsData.map((pe) => ({
                      id: pe.primary_event_id,
                      name: pe.event_name,
                      abbreviation: pe.event_abbreviation,
                    })),
                  };
                }
              }
            } catch (err) {
              console.error("Error fetching all event configuration:", err);
            }
          }

          const processedEvent = {
            ...event,
            fees: fees,
            calculationTypeName:
              event.tournamentEventCalculationType?.name || null,
            calculationTypeCode:
              event.tournamentEventCalculationType?.code || null,
            // Entry rows match the prize-fund multiplier for regular events.
            // All-events are replaced with the distinct bowler count below.
            bowlerCount: eventEntryCount || 0,
            teamCount: (teamCount || 0) + (eventEntryCount || 0),
            allEventConfig: allEventConfig,
          };
          return processedEvent;
        });

      // Wait for all async operations to complete
      const processedEvents = await Promise.all(eventsWithFees);

      // Process templates to include fees
      const templatesWithFees = templatesData.map((template) => {
        const templateFees = templateFeesData.filter(
          (fee) => fee.templateID === template.id
        );
        const fees = templateFees.map((fee) => ({
          id: fee.id,
          amount: fee.amount,
          feeName: fee.fees?.feeName,
        }));

        return {
          ...template,
          isTemplate: true,
          isPrimary: false, // Templates are never primary
          bowlerQuantity: 1, // Templates are always individual events
          gamesPerEvent: null, // Templates don't have games per event
          fees: fees,
          calculationTypeName:
            template.tournamentEventCalculationType?.name || null,
          calculationTypeCode:
            template.tournamentEventCalculationType?.code || null,
          teamCount: 0, // Templates don't have teams assigned
        };
      });

      // Prize fund is per bowler. Use the same bowler_count Finance uses,
      // and keep each squad event separate so a template is not one combined board.
      let statsById = {};
      try {
        const { data: stats, error: statsError } = await supabase.rpc(
          "get_event_entry_statistics",
          { input_tournament_id: tournamentId }
        );
        if (!statsError && Array.isArray(stats)) {
          stats.forEach((row) => {
            statsById[row.event_id] = row;
          });
        }
      } catch (statsErr) {
        console.error("Error fetching bowler counts:", statsErr);
      }

      processedEvents.forEach((event) => {
        const stat = statsById[event.id];
        if (stat && stat.bowler_count != null) {
          event.bowlerCount = Number(stat.bowler_count) || 0;
        }
      });

      let squadInstances = [];
      try {
        const { data, error } = await supabase
          .from("tournamentEvents")
          .select("id, templateId")
          .eq("tournamentID", tournamentId)
          .eq("isDeleted", false)
          .not("templateId", "is", null);
        if (!error) squadInstances = data || [];
      } catch (instanceErr) {
        console.error("Error fetching squad events for fee estimates:", instanceErr);
      }

      const squadsByTemplate = {};
      squadInstances.forEach((row) => {
        const stat = statsById[row.id];
        if (!row.templateId || !stat) return;
        if (!squadsByTemplate[row.templateId]) {
          squadsByTemplate[row.templateId] = [];
        }
        squadsByTemplate[row.templateId].push({
          name: stat.event_name,
          bowlerCount: Number(stat.bowler_count) || 0,
          bowlerQuantity: Number(stat.bowler_quantity) || 1,
        });
      });

      templatesWithFees.forEach((template) => {
        template.estimateEvents = squadsByTemplate[template.id] || [];
      });

      // Combine events and templates, sort by type (events first, then templates)
      const allItems = [...processedEvents, ...templatesWithFees];
      setEvents(allItems);
      setErrorMessage("");
    } catch (error) {
      setErrorMessage("Failed to load events");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (updatedEvent) => {
    try {
      // Handle empty string or "undefined" string for calculation type ID
      const calculationTypeId = updatedEvent.tournamentEventCalculationTypeID;
      const processedCalculationTypeId =
        calculationTypeId &&
        calculationTypeId !== "" &&
        calculationTypeId !== "undefined"
          ? calculationTypeId
          : null;

      const isTemplate =
        updatedEvent.isTemplate === true ||
        (updatedEvent.templateId !== undefined &&
          updatedEvent.templateId !== null);

      if (isTemplate) {
        // Update template
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) {
          throw new Error("User not authenticated");
        }

        const updateData = {
          eventName: updatedEvent.eventName,
          eventAbbreviation: updatedEvent.eventAbbreviation,
          tournamentEventCalculationTypeID: processedCalculationTypeId,
          maxSharedPlayers:
            updatedEvent.maxSharedPlayers === undefined
              ? null
              : updatedEvent.maxSharedPlayers,
          updatedBy: user.id,
          updatedAt: new Date().toISOString(),
        };

        // Check if template has instances before updating
        const { data: instancesData, error: instancesError } = await supabase
          .from("tournamentEvents")
          .select("id")
          .eq("templateId", updatedEvent.id)
          .eq("isDeleted", false)
          .limit(1);

        const hasInstances = instancesData && instancesData.length > 0;

        // Use cascade update function if instances exist, otherwise regular update
        if (hasInstances) {
          const cascadeParams = {
            p_template_id: updatedEvent.id,
            p_event_name: updatedEvent.eventName,
            p_event_abbreviation: updateData.eventAbbreviation,
            p_calculation_type_id: processedCalculationTypeId,
            p_max_shared_players:
              updateData.maxSharedPlayers === undefined
                ? null
                : updateData.maxSharedPlayers,
            p_user_id: user.id,
          };

          const { data: cascadeResult, error: cascadeError } =
            await supabase.rpc("update_template_with_cascade", cascadeParams);

          if (cascadeError) {
            const sanitizedMessage = sanitizeOperationError.update(
              cascadeError,
              "event template",
              isPayoutFinalized
            );
            logErrorForDebugging(
              cascadeError,
              "template cascade update",
              sanitizedMessage
            );
            throw new Error(sanitizedMessage);
          }

          // Verify the cascade update succeeded
          // cascadeResult should be true if successful, false if no rows updated, or null if error
          if (cascadeResult === false || cascadeResult === null) {
            if (cascadeResult === null) {
              // Function might have failed silently - check if template was actually updated
              const { data: checkData } = await supabase
                .from("eventSquadTemplate")
                .select("id")
                .eq("id", updatedEvent.id)
                .eq("isDeleted", false)
                .single();

              if (!checkData) {
                throw new Error(
                  "Update failed: Template not found or could not be updated."
                );
              }
              // If template exists, assume update succeeded (function might have issue returning value)
            } else {
              throw new Error("Update failed: No template was updated.");
            }
          }
        } else {
          const { data, error } = await supabase
            .from("eventSquadTemplate")
            .update(updateData)
            .eq("id", updatedEvent.id)
            .select();

          if (error) {
            const sanitizedMessage = sanitizeOperationError.update(
              error,
              "event template",
              isPayoutFinalized
            );
            logErrorForDebugging(error, "template update", sanitizedMessage);
            throw new Error(sanitizedMessage);
          }

          // Verify the update actually succeeded
          if (!data || data.length === 0) {
            const message = isPayoutFinalized
              ? "Cannot update event template: Payouts have been locked and cannot be modified."
              : "Update failed: No template was updated.";
            throw new Error(message);
          }
        }
      } else {
        // Update tournament event
        const updateData = {
          eventName: updatedEvent.eventName,
          isPrimary: updatedEvent.isPrimary,
          bowlerQuantity: updatedEvent.isPrimary
            ? updatedEvent.bowlerQuantity
            : 1,
          eventAbbreviation: updatedEvent.eventAbbreviation,
          tournamentEventCalculationTypeID: processedCalculationTypeId,
          maxSharedPlayers:
            updatedEvent.maxSharedPlayers === undefined
              ? null
              : updatedEvent.maxSharedPlayers,
          gamesPerEvent: updatedEvent.isPrimary
            ? Number(updatedEvent.gamesPerEvent) || 3
            : null,
        };

        const { data, error } = await supabase
          .from("tournamentEvents")
          .update(updateData)
          .eq("id", updatedEvent.id)
          .select();

        if (error) {
          // Use sanitized error handling
          const sanitizedMessage = sanitizeOperationError.update(
            error,
            "event",
            isPayoutFinalized
          );
          logErrorForDebugging(error, "event update", sanitizedMessage);
          throw new Error(sanitizedMessage);
        }

        // Verify the update actually succeeded by checking if data was returned
        if (!data || data.length === 0) {
          const message = isPayoutFinalized
            ? "Cannot update event: Payouts have been locked and cannot be modified."
            : "Update failed: No changes were made to the event.";
          throw new Error(message);
        }

        // Handle all event configuration if present (only for tournament events)
        if (updatedEvent.allEventConfig) {
          await saveAllEventConfiguration(
            updatedEvent.id,
            updatedEvent.allEventConfig
          );
        }
      }

      // UI reflects changes; no toast
      setErrorMessage("");
      fetchEvents();
      onEventsChange?.();
    } catch (error) {
      setErrorMessage(error.message);
    }
  };

  const handleDelete = async (eventId, isTemplate = false) => {
    try {
      // Get current user for audit trail
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        throw new Error("User not authenticated");
      }

      if (isTemplate) {
        await deleteOrphanedTemplateInstances(eventId);
      }

      // Soft delete - RLS will prevent deletion if instances/teams are assigned
      const updatePayload = {
        isDeleted: true,
        deletedAt: new Date().toISOString(),
        deletedBy: user.id,
      };

      const { data: softDeletedItem, error: itemError } = isTemplate
        ? await supabase
            .from("eventSquadTemplate")
            .update(updatePayload)
            .eq("id", eventId)
            .select()
        : await supabase
            .from("tournamentEvents")
            .update(updatePayload)
            .eq("id", eventId)
            .select();

      if (itemError) {
        // Check if the error is due to RLS policy violation
        if (
          itemError.message.includes(
            "new row violates row-level security policy"
          )
        ) {
          throw new Error(
            `Cannot delete ${
              isTemplate ? "template" : "event"
            }: Row-level security policy violation. This could be due to ${
              isTemplate ? "instances" : "teams"
            } being assigned, insufficient permissions, or other policy restrictions.`
          );
        }

        const sanitizedMessage = sanitizeOperationError.update(
          itemError,
          isTemplate ? "event template" : "event",
          isPayoutFinalized
        );
        logErrorForDebugging(
          itemError,
          isTemplate ? "soft delete template" : "soft delete event",
          sanitizedMessage
        );
        throw new Error(sanitizedMessage);
      }

      // Verify the soft deletion actually succeeded
      if (!softDeletedItem || softDeletedItem.length === 0) {
        const message = isPayoutFinalized
          ? `Cannot delete ${
              isTemplate ? "template" : "event"
            }: Payouts have been locked and cannot be modified.`
          : `Soft delete failed: No ${
              isTemplate ? "template" : "event"
            } was updated.`;
        throw new Error(message);
      }

      // UI reflects deletion
      setErrorMessage("");
      fetchEvents();
      onEventsChange?.();
    } catch (error) {
      setErrorMessage(error.message);
    }
  };

  const createEventRecord = async (eventData) => {
    const calculationTypeId = eventData.tournamentEventCalculationTypeID;
    const processedCalculationTypeId =
      calculationTypeId &&
      calculationTypeId !== "" &&
      calculationTypeId !== "undefined"
        ? calculationTypeId
        : null;

    const isPerSquad = eventData.eventScope === "squad";
    const fees =
      eventData.fees && eventData.fees.length > 0
        ? eventData.fees
        : filledEventFees(
            eventData.isPrimary ? primaryFeeAmounts : sidepotFeeAmounts
          );

    const sharedData = {
      eventName: eventData.eventName ? sanitizeText(eventData.eventName) : "",
      eventAbbreviation: eventData.eventAbbreviation
        ? sanitizeText(eventData.eventAbbreviation)
        : null,
      tournamentEventCalculationTypeID: processedCalculationTypeId,
      tournamentID: tournamentId,
      maxSharedPlayers:
        eventData.maxSharedPlayers === undefined
          ? null
          : eventData.maxSharedPlayers,
    };

    if (isPerSquad) {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        throw new Error("User not authenticated");
      }

      const templateData = {
        ...sharedData,
        createdBy: user.id,
        updatedBy: user.id,
      };

      const { data, error } = await supabase
        .from("eventSquadTemplate")
        .insert([templateData])
        .select();

      if (error) {
        const sanitizedMessage = sanitizeOperationError.create(
          error,
          "event template",
          isPayoutFinalized
        );
        logErrorForDebugging(error, "create event template", sanitizedMessage);
        throw new Error(sanitizedMessage);
      }

      if (!data || data.length === 0) {
        const message = isPayoutFinalized
          ? "Cannot create event template: Payouts have been locked and cannot be modified."
          : "Creation failed: No event template was created.";
        throw new Error(message);
      }

      if (fees.length > 0) {
        const templateFees = fees.map((fee) => ({
          templateID: data[0].id,
          feeID: fee.feeID,
          amount: fee.amount,
        }));

        const { error: feesError } = await supabase
          .from("eventSquadTemplateFees")
          .insert(templateFees);

        if (feesError) {
          console.error("Error creating template fees:", feesError);
        }
      }

      return data[0];
    }

    const processedData = {
      ...sharedData,
      isPrimary: eventData.isPrimary,
      bowlerQuantity: eventData.isPrimary ? eventData.bowlerQuantity : 1,
      maxSharedPlayers:
        eventData.maxSharedPlayers === undefined
          ? null
          : eventData.maxSharedPlayers,
      gamesPerEvent: eventData.isPrimary
        ? Number(eventData.gamesPerEvent) || 3
        : null,
    };

    const { data, error } = await supabase
      .from("tournamentEvents")
      .insert([processedData])
      .select();

    if (error) {
      const sanitizedMessage = sanitizeOperationError.create(
        error,
        "event",
        isPayoutFinalized
      );
      logErrorForDebugging(error, "create event", sanitizedMessage);
      throw new Error(sanitizedMessage);
    }

    if (!data || data.length === 0) {
      const message = isPayoutFinalized
        ? "Cannot create event: Payouts have been locked and cannot be modified."
        : "Creation failed: No event was created.";
      throw new Error(message);
    }

    const createdEvent = data[0];

    if (eventData.allEventConfig?.primaryEventIds?.length) {
      await saveAllEventConfiguration(createdEvent.id, eventData.allEventConfig);
    }

    if (fees.length > 0) {
      const eventFees = fees.map((fee) => ({
        eventsID: createdEvent.id,
        feeID: fee.feeID,
        amount: fee.amount,
      }));

      const { error: feesError } = await supabase
        .from("eventFee")
        .insert(eventFees);

      if (feesError) {
        console.error("Error creating event fees:", feesError);
      }
    }

    return createdEvent;
  };

  const handleCreateEvent = async (eventData) => {
    try {
      await createEventRecord(eventData);
      setShowNewEventForm(false);
      setErrorMessage("");
      fetchEvents();
      onEventsChange?.();
    } catch (error) {
      setErrorMessage(error.message);
    }
  };

  const handleCreateTemplates = async (items) => {
    try {
      const primaries = items.filter((item) => item.template.group === "primary");
      const sidepots = items.filter((item) => item.template.group === "sidepot");
      const createdPrimaryIds = [];

      for (const item of primaries) {
        const created = await createEventRecord({
          ...item.payload,
          fees: filledEventFees(primaryFeeAmounts),
        });
        createdPrimaryIds.push(created.id);
      }

      const primaryEventIds = [
        ...events.filter(isPrimaryEvent).map((event) => event.id),
        ...createdPrimaryIds,
      ];

      for (const item of sidepots) {
        await createEventRecord({
          ...item.payload,
          fees: filledEventFees(sidepotFeeAmounts),
          ...(isAllEventTemplate(item.template) && {
            allEventConfig: {
              primaryEventIds,
              secondaryRuleType: "first_attempt",
              isValid: true,
            },
          }),
        });
      }

      setErrorMessage("");
      await fetchEvents();
      onEventsChange?.();
    } catch (error) {
      setErrorMessage(error.message);
      fetchEvents();
    }
  };

  const handleApplyGroupFees = async (group) => {
    if (isPayoutFinalized) return;
    const isPrimaryGroup = group === "primary";
    const groupEvents = events.filter(
      isPrimaryGroup ? isPrimaryEvent : isSidePotEvent
    );
    const amounts = isPrimaryGroup ? primaryFeeAmounts : sidepotFeeAmounts;
    setSavingFeeGroup(group);
    try {
      for (const event of groupEvents) {
        await syncEventFees({
          event,
          amounts,
          catalog: feeCatalog,
          isPayoutFinalized,
        });
      }
      if (isPrimaryGroup) setPrimaryFeesDirty(false);
      else setSidepotFeesDirty(false);
      setErrorMessage("");
      await fetchEvents();
    } catch (error) {
      const sanitizedMessage = sanitizeOperationError.update(
        error,
        "fee",
        isPayoutFinalized
      );
      logErrorForDebugging(error, "apply group fees", sanitizedMessage);
      setErrorMessage(error.message || sanitizedMessage);
      throw error;
    } finally {
      setSavingFeeGroup(null);
    }
  };

  const handleSaveEventFees = async (event, amounts) => {
    if (isPayoutFinalized) return;
    try {
      await syncEventFees({
        event,
        amounts,
        catalog: feeCatalog,
        isPayoutFinalized,
      });
      setErrorMessage("");
      await fetchEvents();
    } catch (error) {
      const sanitizedMessage = sanitizeOperationError.update(
        error,
        "fee",
        isPayoutFinalized
      );
      logErrorForDebugging(error, "save event fees", sanitizedMessage);
      setErrorMessage(error.message || sanitizedMessage);
      throw error;
    }
  };

  // Function to save/update all event configuration
  const saveAllEventConfiguration = async (eventId, config) => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        throw new Error("User not authenticated");
      }

      // Check if configuration already exists
      const { data: existingConfig, error: fetchError } = await supabase.rpc(
        "get_all_event_configuration",
        { input_event_id: eventId }
      );

      if (fetchError && !fetchError.message.includes("does not exist")) {
        throw fetchError;
      }

      const configData = {
        eventId: eventId,
        primaryEventIds: config.primaryEventIds,
        secondaryRuleType: config.secondaryRuleType,
        updatedBy: user.id,
      };

      if (existingConfig && existingConfig.length > 0) {
        // Update existing configuration
        const { error: updateError } = await supabase
          .from("tournamentAllEventConfigurations")
          .update(configData)
          .eq("eventId", eventId)
          .eq("isDeleted", false);

        if (updateError) throw updateError;
      } else {
        // Create new configuration
        const { error: insertError } = await supabase
          .from("tournamentAllEventConfigurations")
          .insert([
            {
              ...configData,
              createdBy: user.id,
            },
          ]);

        if (insertError) throw insertError;
      }
    } catch (error) {
      console.error("Error saving all event configuration:", error);
      throw new Error(
        `Failed to save all event configuration: ${error.message}`
      );
    }
  };

  if (isLoading || authLoading) {
    return (
      <div className="card-board p-6 mb-6">
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="h-6 bg-brand-cream rounded w-1/3 mb-4"></div>
              <div className="space-y-2">
                <div className="h-4 bg-brand-cream rounded w-1/2"></div>
                <div className="h-4 bg-brand-cream rounded w-1/4"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="card-board p-6 mb-6">
      {errorMessage ? (
        <div className="mb-4">
          <InlineAlert
            type="error"
            title="There was a problem"
            message={errorMessage}
            onClose={() => setErrorMessage("")}
          />
        </div>
      ) : null}
      <div className="flex justify-between items-center mb-6">
        <h2 className="heading-display text-xl">
          Events and Fees
        </h2>
        {canManage && (
          <SecondaryButton
            onClick={() => setShowNewEventForm(true)}
            disabled={isPayoutFinalized}
            className={
              isPayoutFinalized
                ? "opacity-50 cursor-not-allowed !px-3 !py-2 text-sm"
                : "!px-3 !py-2 text-sm"
            }
            title={
              isPayoutFinalized
                ? "Payouts have been locked and cannot be changed"
                : "Add a custom event"
            }
          >
            <FaPlus className="mr-2 h-4 w-4" />
            Custom Event
          </SecondaryButton>
        )}
      </div>

      {/* Payout Locking Warning */}
      {isPayoutFinalized && (
        <div className="mb-4 card-inset p-3">
          <p className="text-sm text-brand-muted">
            Payouts have been locked. Events and fees cannot be modified.
          </p>
        </div>
      )}

      {canManage && (
        <EventTemplatePicker
          events={events}
          onCreateMany={handleCreateTemplates}
          disabled={isPayoutFinalized}
          catalog={feeCatalog}
          primaryAmounts={primaryFeeAmounts}
          sidepotAmounts={sidepotFeeAmounts}
          onPrimaryAmountChange={(feeId, amount) => {
            setPrimaryFeesDirty(true);
            setPrimaryFeeAmounts((prev) => ({ ...prev, [feeId]: amount }));
          }}
          onSidepotAmountChange={(feeId, amount) => {
            setSidepotFeesDirty(true);
            setSidepotFeeAmounts((prev) => ({ ...prev, [feeId]: amount }));
          }}
        />
      )}

      {showNewEventForm && (
        <div className="mb-6 p-6 card-inset">
          <div className="flex justify-between items-center mb-4">
            <h3 className="heading-display text-lg">Custom Event</h3>
          </div>
          <EventForm
            tournamentId={tournamentId}
            onSubmit={handleCreateEvent}
            onCancel={() => setShowNewEventForm(false)}
          />
        </div>
      )}

      {events.length === 0 ? (
        canManage ? null : (
          <div className="text-center py-12">
            <FaBowlingBall className="mx-auto h-12 w-12 text-brand-muted" />
            <h3 className="mt-2 heading-display text-lg">
              No events yet
            </h3>
            <p className="mt-1 text-sm text-brand-muted">
              Get started by adding your first event.
            </p>
          </div>
        )
      ) : (
        <>
          <EventGroupSection
            title="Primary events"
            events={events.filter(isPrimaryEvent)}
            catalog={feeCatalog}
            amounts={primaryFeeAmounts}
            onAmountChange={(feeId, amount) => {
              setPrimaryFeesDirty(true);
              setPrimaryFeeAmounts((prev) => ({ ...prev, [feeId]: amount }));
            }}
            onApply={() => handleApplyGroupFees("primary")}
            onCancelEdit={() => {
              setPrimaryFeesDirty(false);
              const first = events.find(isPrimaryEvent);
              if (first) {
                setPrimaryFeeAmounts(amountsFromEvent(first, feeCatalog));
              }
            }}
            saving={savingFeeGroup === "primary"}
            canManage={canManage}
            disabled={isPayoutFinalized}
          >
            {events.filter(isPrimaryEvent).map((event) => (
              <EventCard
                key={event.id}
                event={event}
                onSave={handleSave}
                onDelete={(id) => {
                  const match = events.find((item) => item.id === id);
                  handleDelete(id, match?.isTemplate);
                }}
                onSaveFees={handleSaveEventFees}
                catalog={feeCatalog}
                isLoggedIn={canManage}
                isPayoutFinalized={isPayoutFinalized}
              />
            ))}
          </EventGroupSection>
          <EventGroupSection
            title="Side pots"
            events={events.filter(isSidePotEvent)}
            catalog={feeCatalog}
            amounts={sidepotFeeAmounts}
            onAmountChange={(feeId, amount) => {
              setSidepotFeesDirty(true);
              setSidepotFeeAmounts((prev) => ({ ...prev, [feeId]: amount }));
            }}
            onApply={() => handleApplyGroupFees("sidepot")}
            onCancelEdit={() => {
              setSidepotFeesDirty(false);
              const first = events.find(isSidePotEvent);
              if (first) {
                setSidepotFeeAmounts(amountsFromEvent(first, feeCatalog));
              }
            }}
            saving={savingFeeGroup === "sidepot"}
            canManage={canManage}
            disabled={isPayoutFinalized}
          >
            {events.filter(isSidePotEvent).map((event) => (
              <EventCard
                key={event.id}
                event={event}
                onSave={handleSave}
                onDelete={(id) => {
                  const match = events.find((item) => item.id === id);
                  handleDelete(id, match?.isTemplate);
                }}
                onSaveFees={handleSaveEventFees}
                catalog={feeCatalog}
                isLoggedIn={canManage}
                isPayoutFinalized={isPayoutFinalized}
              />
            ))}
          </EventGroupSection>
          <EventGroupSection
            title="Per-squad side pots"
            description="These spawn a separate event on each squad you attach them to. Tournament-wide side pots are not listed here."
            events={events.filter(isPerSquadSidePot)}
            catalog={feeCatalog}
            amounts={sidepotFeeAmounts}
            onAmountChange={() => {}}
            onApply={async () => {}}
            canManage={canManage}
            disabled={isPayoutFinalized}
            showGroupFees={false}
          >
            {events.filter(isPerSquadSidePot).map((event) => (
              <EventCard
                key={event.id}
                event={event}
                onSave={handleSave}
                onDelete={(id) => {
                  const match = events.find((item) => item.id === id);
                  handleDelete(id, match?.isTemplate);
                }}
                onSaveFees={handleSaveEventFees}
                catalog={feeCatalog}
                isLoggedIn={canManage}
                isPayoutFinalized={isPayoutFinalized}
              />
            ))}
          </EventGroupSection>
        </>
      )}
    </div>
  );
};

export default ManageEvents;
